import React from 'react';
import PropTypes from 'prop-types';
import {
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Alert,
    AlertTitle,
    Box
} from '@mui/material';
import { LoadingButton } from '@mui/lab';

import { hasError, formatError } from '../../utils/formatError';
import i18n from '../../i18n';
import Fields from '../editors';

export default class CreateTransactionDialog extends React.Component {
    type;
    constructor(props) {
        super(props);
        this.state = {
            dialogData: {},
            alertText: '',
            alertTitle: '',
            loading: false,
            link: null
        };
        this.type = props.type || 'default';
    }

    static buildEmptyData(Head) {
        const data = {};
        if (Array.isArray(Head)) {
            Head.forEach((field) => {
                data[field.id] = 'default' in field ? field.default : '';
            });
        }
        return data;
    }

    componentDidMount() {
        if (this.props.dialogOpen) {
            this.initializeDialogData();
        }
    }
    componentDidUpdate(prevProps) {
        if (!prevProps.dialogOpen && this.props.dialogOpen) {
            this.initializeDialogData();
        }
    }

    initializeDialogData = () => {
        const data = CreateTransactionDialog.buildEmptyData(this.props.Head);
        const hasPrevData =
            this.props.enablePrevData &&
            !!localStorage.getItem(
                `lastDialogData_${this.props.id}_${this.type}`
            );
        this.setState({
            dialogData: data,
            alertText: '',
            alertTitle: '',
            loading: false,
            prevDataLoaded: false,
            hasPrevData
        });
    };

    handleShowLink = (link) => {
        this.setState({
            link: link,
            alertText: '',
            alertTitle: ''
        });
    };
    handleClose = () => {
        this.setState({
            link: null
        });
        this.props.onClose();
    };

    handleDialogSave = async () => {
        this.setState({ loading: true });
        // set last dialog data in the local storage
        localStorage.setItem(
            `lastDialogData_${this.props.id}_${this.type}`,
            JSON.stringify(this.state.dialogData)
        );
        // remove field with onlyRead from dialogData
        const dialogData = { ...this.state.dialogData };
        Object.keys(dialogData).forEach((key) => {
            if (this.props.Head.find((field) => field.id === key)?.onlyRead) {
                delete dialogData[key];
            }
        });
        try {
            const result = await this.props.server(
                this.props.id,
                dialogData
            );
            if (!hasError(result)) {
                result.data && typeof result.data === 'string'
                    ? this.handleShowLink(result.data)
                    : this.handleClose();
            } else {
                this.setState({ alertText: formatError(result) });
            }
        } catch (err) {
            console.error('Save error:', err);
            this.setState({ alertText: i18n.t('text.unexpectedError') });
        } finally {
            this.setState({ loading: false });
        }
    };

    handleValueChanged = (id, value) => {
        this.setState((prevState) => ({
            dialogData: {
                ...prevState.dialogData,
                [id]: value
            },
            prevDataLoaded: false
        }));
    };

    fillPrevDialogData = () => {
        if (!this.props.enablePrevData) return;
        const prevData = localStorage.getItem(
            `lastDialogData_${this.props.id}_${this.type}`
        );
        if (prevData) {
            this.setState({
                dialogData: JSON.parse(prevData),
                alertText: '',
                alertTitle: '',
                prevDataLoaded: true,
                hasPrevData: true
            });
        } else {
            this.initializeDialogData();
        }
    };

    render() {
        const {
            dialogOpen,
            onClose,
            Head = [],
            dialogTitle,
            saveLabel,
            cancelLabel
        } = this.props;

        const { dialogData, alertText, alertTitle, loading, link } = this.state;


        return (
            <Dialog open={dialogOpen} onClose={onClose} maxWidth="sm" fullWidth>
                <DialogTitle>
                    <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                    >
                        {dialogTitle
                            ? i18n.t(dialogTitle)
                            : i18n.t('dialogs.create')}
                        {this.props.enablePrevData && (
                            <Button
                                variant="text"
                                onClick={this.fillPrevDialogData}
                                disabled={
                                    !this.state.hasPrevData ||
                                    this.state.prevDataLoaded
                                }
                            >
                                {i18n.t('buttons.previousData')}
                            </Button>
                        )}
                    </Box>
                </DialogTitle>

                <DialogContent>
                    {alertText && (
                        <Alert severity="error">
                            <AlertTitle>
                                {alertTitle || i18n.t('text.error')}
                            </AlertTitle>
                            {alertText}
                        </Alert>
                    )}

                    {link ? (
                        <Alert severity="success">
                            <AlertTitle>{i18n.t('text.success')}</AlertTitle>
                            {i18n.t('text.orderCreated')}
                            <br />
                            <a
                                href={link}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                {link}
                            </a>
                        </Alert>
                    ) : (
                        Array.isArray(Head) &&
                        Head.map((field) => {
                            const Field = Fields[field.cell] || Fields.Text;

                            return (
                                <Field
                                    key={field.id}
                                    field={field}
                                    value={dialogData?.[field.id]}
                                    onChange={this.handleValueChanged}
                                />
                            );
                        })
                    )}
                </DialogContent>

                <DialogActions>
                    {link ? (
                        <Button onClick={() => this.handleClose()}>
                            {i18n.t('buttons.close')}
                        </Button>
                    ) : (
                        <>
                            <Button disabled={loading} onClick={onClose}>
                                {cancelLabel
                                    ? i18n.t(cancelLabel)
                                    : i18n.t('buttons.cancel')}
                            </Button>
                            <LoadingButton
                                loading={loading}
                                onClick={this.handleDialogSave}
                            >
                                {saveLabel
                                    ? i18n.t(saveLabel)
                                    : i18n.t('buttons.save')}
                            </LoadingButton>
                        </>
                    )}
                </DialogActions>
            </Dialog>
        );
    }
}

CreateTransactionDialog.propTypes = {
    dialogOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    Head: PropTypes.arrayOf(PropTypes.object).isRequired,
    server: PropTypes.func.isRequired,
    id: PropTypes.any.isRequired,
    dialogTitle: PropTypes.string,
    saveLabel: PropTypes.string,
    cancelLabel: PropTypes.string,
    enablePrevData: PropTypes.bool
};
CreateTransactionDialog.defaultProps = {
    enablePrevData: true
};
